import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup } from 'react-bootstrap';

const REQUIRED_FEATURES = [
  { key: 'dms_access', label: 'DMS Access' },
  { key: 'distributor_dashboard', label: 'Distributor Dashboard' },
  { key: 'retailer_payment_system', label: 'Retailer Payment System' },
  { key: 'ca_login', label: 'CA Login' },
  { key: 'compliance_reports', label: 'Compliance Reports' }
];

const PlanFormModal = ({
  show,
  handleClose,
  mode,
  planData,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    price: '',
    billing_cycle: 'monthly',
    duration_days: '',
    max_retailers: '',
    max_monthly_turnover: '',
    max_beat_users: '',
    max_support_ids: '',
    escalation_response_hours: '',
    features: {
      dms_access: true,
      distributor_dashboard: true,
      retailer_payment_system: true,
      ca_login: true,
      compliance_reports: true
    },
    status: 'active'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getDefaultFeatures = () => ({
    dms_access: true,
    distributor_dashboard: true,
    retailer_payment_system: true,
    ca_login: true,
    compliance_reports: true
  });

  useEffect(() => {
    if (show) {
      if (mode === 'edit' && planData) {
        const existingFeatures = planData.features_json || {};
        setFormData({
          code: planData.code || '',
          name: planData.name || '',
          price: planData.price || '',
          billing_cycle: planData.billing_cycle || 'monthly',
          duration_days: planData.duration_days || '',
          max_retailers: planData.max_retailers || '',
          max_monthly_turnover: planData.max_monthly_turnover || '',
          max_beat_users: planData.max_beat_users || '',
          max_support_ids: planData.max_support_ids || '',
          escalation_response_hours: planData.escalation_response_hours || '',
          features: {
            dms_access: existingFeatures.dms_access ?? true,
            distributor_dashboard: existingFeatures.distributor_dashboard ?? true,
            retailer_payment_system: existingFeatures.retailer_payment_system ?? true,
            ca_login: existingFeatures.ca_login ?? true,
            compliance_reports: existingFeatures.compliance_reports ?? true
          },
          status: planData.status || 'active'
        });
      } else {
        setFormData({
          code: '',
          name: '',
          price: '',
          billing_cycle: 'monthly',
          duration_days: '30',
          max_retailers: '',
          max_monthly_turnover: '',
          max_beat_users: '',
          max_support_ids: '',
          escalation_response_hours: '',
          features: getDefaultFeatures(),
          status: 'active'
        });
      }
      setErrors({});
    }
  }, [mode, planData, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFeatureToggle = (featureKey) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [featureKey]: !prev.features[featureKey]
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code?.trim()) {
      newErrors.code = 'Plan code is required';
    } else if (!/^[A-Z0-9_]+$/.test(formData.code.trim())) {
      newErrors.code = 'Code must be uppercase letters, numbers, and underscores only';
    }

    if (!formData.name?.trim()) {
      newErrors.name = 'Plan name is required';
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }

    if (!formData.billing_cycle) {
      newErrors.billing_cycle = 'Billing cycle is required';
    }

    if (!formData.duration_days || parseInt(formData.duration_days) <= 0) {
      newErrors.duration_days = 'Duration must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const dataToSave = {
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
      price: parseFloat(formData.price),
      billing_cycle: formData.billing_cycle,
      duration_days: parseInt(formData.duration_days),
      status: formData.status
    };

    // Add optional fields only if they have values
    if (formData.max_retailers) {
      dataToSave.max_retailers = parseInt(formData.max_retailers);
    }
    if (formData.max_monthly_turnover) {
      dataToSave.max_monthly_turnover = parseFloat(formData.max_monthly_turnover);
    }
    if (formData.max_beat_users) {
      dataToSave.max_beat_users = parseInt(formData.max_beat_users);
    }
    if (formData.max_support_ids) {
      dataToSave.max_support_ids = parseInt(formData.max_support_ids);
    }
    if (formData.escalation_response_hours) {
      dataToSave.escalation_response_hours = parseInt(formData.escalation_response_hours);
    }

    // Always include features_json with all required keys
    dataToSave.features_json = formData.features;

    try {
      const result = await onSave(dataToSave);
      if (result?.success) {
        handleModalClose();
      } else if (result?.error) {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      setErrors({ submit: 'Failed to save plan' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setFormData({
      code: '',
      name: '',
      price: '',
      billing_cycle: 'monthly',
      duration_days: '30',
      max_retailers: '',
      max_monthly_turnover: '',
      max_beat_users: '',
      max_support_ids: '',
      escalation_response_hours: '',
      features: getDefaultFeatures(),
      status: 'active'
    });
    setErrors({});
    setIsSubmitting(false);
    handleClose();
  };

  const handleBillingCycleChange = (cycle) => {
    const durationMap = {
      monthly: '30',
      quarterly: '90',
      yearly: '365'
    };
    setFormData(prev => ({
      ...prev,
      billing_cycle: cycle,
      duration_days: durationMap[cycle] || prev.duration_days
    }));
  };

  return (
    <Modal show={show} onHide={handleModalClose} centered backdrop="static" dialogClassName="modal-90w">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={`bi ${mode === 'add' ? 'bi-plus-circle' : 'bi-pencil-square'} me-2`}></i>
          {mode === 'add' ? 'Add New Plan' : 'Edit Plan'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {errors.submit && (
            <div className="alert alert-danger mb-3">{errors.submit}</div>
          )}

          <Row>
            {/* Plan Code */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Plan Code <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  isInvalid={!!errors.code}
                  disabled={isSubmitting || mode === 'edit'}
                  placeholder="e.g., STARTER_MONTHLY"
                  style={{ textTransform: 'uppercase' }}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.code}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Unique identifier (uppercase, no spaces)
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Plan Name */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Plan Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  isInvalid={!!errors.name}
                  disabled={isSubmitting}
                  placeholder="e.g., Starter Plan"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Price */}
            <Col md={4} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Price <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>INR</InputGroup.Text>
                  <Form.Control
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    isInvalid={!!errors.price}
                    disabled={isSubmitting}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </InputGroup>
                <Form.Control.Feedback type="invalid">
                  {errors.price}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Billing Cycle */}
            <Col md={4} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Billing Cycle <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="billing_cycle"
                  value={formData.billing_cycle}
                  onChange={(e) => handleBillingCycleChange(e.target.value)}
                  isInvalid={!!errors.billing_cycle}
                  disabled={isSubmitting}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.billing_cycle}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Duration Days */}
            <Col md={4} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Duration (Days) <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="duration_days"
                  value={formData.duration_days}
                  onChange={handleChange}
                  isInvalid={!!errors.duration_days}
                  disabled={isSubmitting}
                  placeholder="30"
                  min="1"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.duration_days}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={12}>
              <hr />
              <h6 className="text-muted mb-3">Plan Limits (Leave empty for unlimited)</h6>
            </Col>

            {/* Max Retailers */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Max Retailers</Form.Label>
                <Form.Control
                  type="number"
                  name="max_retailers"
                  value={formData.max_retailers}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder="Unlimited"
                  min="0"
                />
              </Form.Group>
            </Col>

            {/* Max Monthly Turnover */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Max Monthly Turnover</Form.Label>
                <InputGroup>
                  <InputGroup.Text>INR</InputGroup.Text>
                  <Form.Control
                    type="number"
                    name="max_monthly_turnover"
                    value={formData.max_monthly_turnover}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    placeholder="Unlimited"
                    min="0"
                    step="0.01"
                  />
                </InputGroup>
              </Form.Group>
            </Col>

            {/* Max Beat Users */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Max Beat Users (FOS)</Form.Label>
                <Form.Control
                  type="number"
                  name="max_beat_users"
                  value={formData.max_beat_users}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder="Unlimited"
                  min="0"
                />
              </Form.Group>
            </Col>

            {/* Max Support IDs */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Max Support IDs</Form.Label>
                <Form.Control
                  type="number"
                  name="max_support_ids"
                  value={formData.max_support_ids}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder="Unlimited"
                  min="0"
                />
              </Form.Group>
            </Col>

            <Col xs={12}>
              <hr />
              <h6 className="text-muted mb-3">SLA Settings</h6>
            </Col>

            {/* Escalation Response Hours */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Escalation Response Time (Hours)</Form.Label>
                <Form.Control
                  type="number"
                  name="escalation_response_hours"
                  value={formData.escalation_response_hours}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder="e.g., 24"
                  min="1"
                />
                <Form.Text className="text-muted">
                  Maximum response time for support escalations
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Status */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Additional Features */}
            <Col xs={12}>
              <hr />
              <h6 className="text-muted mb-3">
                Additional Features <span className="text-danger">*</span>
              </h6>
            </Col>
            {REQUIRED_FEATURES.map((feature) => (
              <Col md={6} lg={4} key={feature.key} className="mb-3">
                <Form.Check
                  type="switch"
                  id={`feature-${feature.key}`}
                  label={feature.label}
                  checked={formData.features[feature.key]}
                  onChange={() => handleFeatureToggle(feature.key)}
                  disabled={isSubmitting}
                />
              </Col>
            ))}
          </Row>
        </Modal.Body>

        <Modal.Footer style={{ borderTop: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : mode === 'add' ? 'Create Plan' : 'Update Plan'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PlanFormModal;
